import React from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";

import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import usePopups from "@app/analytics/hooks/usePopups";
import * as Events from "@app/analytics/tracking/events";
import useAuth from "@app/common/auth/useAuth";
import { PRODUCTIVITY_PROVIDERS } from "@app/common/constants/providers";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import { ProductivityProvider } from "@app/common/types/providerTypes";

import Button from "@rui/Button";
import { NewBadge } from "@rui/FeatureBadge";
import Stack, { Row } from "@rui/Stack";

import HeroImg from "./assets/ProductivityHero.png";
import {
  FeaturePopupContentTitle,
  FeaturePopupContentDescription,
  FeaturePopupHeroContainer,
  FeaturePopupCloseButton,
  FeaturePopupContent,
  FeaturePopupCard,
} from "./styles";

const ProviderButton = ({
  provider,
  onAuthorize,
}: {
  provider: ProductivityProvider;
  onAuthorize: () => void;
}) => {
  const { authorize } = useIntegration();

  const handleClick = () => {
    Events.featurePopupIntegration({ provider: provider.displayNameShort });
    onAuthorize();
    return authorize.mutate({
      providerId: provider.id,
      returnRoute: provider.integrationUrl,
      scope: "user",
    });
  };

  return (
    <Button
      variant="outlined"
      startIcon={
        <Box component="img" src={provider.icon} sx={{ width: "24px" }} />
      }
      onClick={handleClick}
      sx={{
        flexBasis: "145px",
        height: "50px",
        color: "shades.neutral.700",
        border: (theme) => `1px solid ${theme.palette.shades.neutral[200]}`,
      }}
    >
      {provider.displayNameShort}
    </Button>
  );
};

const ProductivityFeaturePopup = ({
  priority,
  onDismissed,
}: {
  priority: number;
  onDismissed: (shouldNavigate: boolean) => void;
}) => {
  const { user } = useAuth();

  const { show, reset } = usePopups({
    id: "productivity",
    priority,
    shouldShow: Boolean(user),
  });

  if (!show || !user) return null;

  const handleDismiss = () => {
    onDismissed(true);
    reset();
  };

  return (
    <Modal open onClose={handleDismiss} disableAutoFocus>
      <FeaturePopupCard
        sx={{
          height: { xxs: "auto", sm: "400px" },
          width: { xxs: "350px", sm: "700px" },
        }}
        layout="horizontal"
      >
        <FeaturePopupHeroContainer
          width={{ xxs: "100%", sm: "50%" }}
          flexShrink={0}
          height="100%"
        >
          <Box
            component="img"
            src={HeroImg}
            sx={{
              width: { xxs: "100%", sm: "350px" },
              display: { xxs: "none", sm: "block" },
            }}
          />
          <FeaturePopupCloseButton onClick={handleDismiss} />
        </FeaturePopupHeroContainer>
        <FeaturePopupContent spacing={2}>
          <Stack spacing={1}>
            <NewBadge />
            <FeaturePopupContentTitle>
              Email and Messaging Readouts
            </FeaturePopupContentTitle>
            <FeaturePopupContentDescription>
              Turn scattered information into actionable intelligence! Readouts
              analyze your communication channels to deliver insights on your
              most important topics.
            </FeaturePopupContentDescription>
            <Divider sx={{ my: 1 }} />
            <Typography
              variant="bodySecondaryMed"
              color="shades.neutral.600"
              textAlign="center"
            >
              Choose a source to connect
            </Typography>
            <Row
              spacing={1}
              flexWrap="wrap"
              justifyContent="center"
              marginTop="4px"
            >
              {PRODUCTIVITY_PROVIDERS.map((provider) => {
                return (
                  <ProviderButton
                    key={provider.id}
                    provider={provider}
                    onAuthorize={() => onDismissed(false)}
                  />
                );
              })}
            </Row>
          </Stack>
        </FeaturePopupContent>
      </FeaturePopupCard>
    </Modal>
  );
};

const ProductivityFeaturePopupGate = (props: { priority: number }) => {
  const [queryParams] = useSearchParams();
  const navigate = useNavigate();

  const [dismissed, setDismissed] = useLocalStorage(
    "productivityFeaturePopupDismissed",
    false
  );

  const handleDismissed = (shouldNavigate: boolean) => {
    queryParams.delete("popup_id");
    setDismissed(true);
    if (shouldNavigate) {
      navigate(
        { pathname: location.pathname, search: queryParams.toString() },
        { replace: true }
      );
    }
  };

  if (dismissed && queryParams.get("popup_id") === null) return null;

  return <ProductivityFeaturePopup onDismissed={handleDismissed} {...props} />;
};

export default ProductivityFeaturePopupGate;
